import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import {
  isLightThemeBackground,
  resolveThemeBackground,
} from '../../../../shared/utils/theme-color.util';
import {
  PlanChangeErrorBody,
  PlanChangeOption,
  PlanChangeOptions,
  PlanChangePlanSummary,
  PlanChangeRefundChoice,
  PlanChangeRefundOption,
  PlanChangeRequest,
  PlanChangeResult,
  PlanChangeService,
  PlanChangeTiming,
} from '../../../../services/plan-change.service';
import { familyListPrice } from './plan-change-panel.family-logic';

export type PlanChangePanelState = 'loading' | 'ineligible' | 'select' | 'processing';

export interface PlanChangeSummaryRow {
  label: string;
  sub: string;
  /** "Updated Plan: <name>" under the label on the renewal row. */
  plan?: string;
  value: string;
  /** Small line under the value, e.g. "Billed monthly". */
  valueSub?: string;
  emphasis: boolean;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * Tile backgrounds for packages with no theme colour of their own, so a shop
 * that never picked colours still gets a readable, varied grid.
 */
const FALLBACK_TILE_BACKGROUNDS = [
  'linear-gradient(160deg, #3F8A6E 0%, #2E6E8C 100%)',
  'linear-gradient(160deg, #5E9A55 0%, #2F5E9C 100%)',
  'linear-gradient(150deg, #3A55E0 0%, #5A2396 100%)',
  'linear-gradient(180deg, #8F8460 0%, #7A6A34 100%)',
  'linear-gradient(150deg, #1D5A48 0%, #061530 100%)',
  'linear-gradient(160deg, #9A6DB0 0%, #6E4E90 100%)',
] as const;

/** Stable API reason codes → attendant copy. Anything else falls back to the server message. */
const REASON_COPY: Record<string, string> = {
  imported_membership: 'This membership is managed by the vendor and cannot be changed here.',
  membership_not_active: 'Only an active membership can be changed.',
  cancellation_scheduled: 'This membership is set to cancel at the end of the cycle.',
  cycle_unknown:
    'The billing cycle for this membership is not known yet. Try again after the next renewal.',
  not_a_monthly_membership: 'Only monthly memberships can be changed.',
  family_plan_not_supported: 'Family plans cannot be changed here.',
  family_plan_disabled:
    'Family plans are switched off at this location, so a family plate cannot change plan.',
  platform_disabled: 'Membership plan changes are not switched on for this environment.',
  location_disabled: 'Membership plan changes are not switched on at this location.',
  renewal_schedule_conflict:
    'This membership has scheduled renewal pricing and cannot be changed here yet.',
  card_declined: 'The saved card was declined. Nothing was changed.',
  authentication_required:
    'The saved card needs the customer to authenticate, so it could not be charged. Nothing was changed.',
  no_card_on_file: 'No saved card was found for this membership. Nothing was changed.',
  no_refundable_charge: 'No card payment was found on this membership to refund against.',
  nothing_refundable: 'The last payment has already been fully refunded.',
  renewal_invoice_exists:
    'The renewal invoice already exists, so this change can no longer be cancelled.',
  membership_changed: 'The membership changed in the meantime. Refresh and try again.',
  price_not_configured: 'The new plan has no recurring price set up yet.',
  renewal_day: 'Today is the renewal day, so only a change from the next cycle is available.',
  pause_in_progress:
    'This membership has a pause in progress and can be changed once billing restarts.',
};

function newRequestId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // RFC 4122 v4 fallback for very old WebViews.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === 'x' ? r : (r % 4) + 8;
    return v.toString(16);
  });
}

/**
 * CWS-1426: the Update Membership screen (SC-6) and its result. Owns the whole
 * flow: load options, pick a plan, pick timing, pick a refund, confirm.
 * Every amount shown comes from the server; the panel only formats it.
 */
@Component({
  selector: 'app-plan-change-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './plan-change-panel.component.html',
  styleUrls: ['./plan-change-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PlanChangePanelComponent implements OnChanges, OnDestroy {
  private readonly planChangeService = inject(PlanChangeService);
  private readonly destroy$ = new Subject<void>();

  @Input() subscriptionId: string | null = null;
  @Input() locationId: string | null = null;
  /** Loads on the transition to true, like the other modal sections. */
  @Input() isActive = false;
  /**
   * Hosts that already offer their own way back (Cancel alone is enough in the
   * Customers drawer) can drop the footer's Back button, which fires the same
   * `back` event Cancel does.
   */
  @Input() showBackButton = true;

  @Output() back = new EventEmitter<void>();
  @Output() applied = new EventEmitter<PlanChangeResult>();

  state: PlanChangePanelState = 'loading';
  options: PlanChangeOptions | null = null;
  ineligibleReason: string | null = null;
  errorMessage: string | null = null;
  result: PlanChangeResult | null = null;

  selected: PlanChangeOption | null = null;
  timing: PlanChangeTiming | null = null;
  refundOption: PlanChangeRefundOption | null = null;
  customRefundDraft = '';
  washOverrideDraft = '';
  isCancelling = false;

  private requestId = newRequestId();

  ngOnChanges(changes: SimpleChanges): void {
    const activated = changes['isActive']?.currentValue === true;
    const target = changes['subscriptionId'] && this.isActive;
    if (activated || target) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------- loading

  load(): void {
    if (!this.subscriptionId || !this.locationId) {
      this.state = 'ineligible';
      this.ineligibleReason = 'cycle_unknown';
      return;
    }
    this.state = 'loading';
    this.errorMessage = null;
    this.result = null;
    this.resetSelection();
    this.planChangeService
      .getOptions(this.subscriptionId, this.locationId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          const body = (err?.error ?? null) as PlanChangeErrorBody | null;
          this.state = 'ineligible';
          this.ineligibleReason = body?.code ?? 'error';
          this.errorMessage = body?.code ? null : body?.message || 'Could not load plan options.';
          return of(null);
        })
      )
      .subscribe(res => {
        if (!res) return;
        if (res.status === false || !res.data) {
          this.state = 'ineligible';
          this.ineligibleReason = 'error';
          this.errorMessage = res.message || 'Could not load plan options.';
          return;
        }
        this.options = res.data;
        if (!res.data.eligible) {
          this.state = 'ineligible';
          this.ineligibleReason = res.data.reason;
          return;
        }
        this.state = 'select';
      });
  }

  // -------------------------------------------------------------- selection

  get current() {
    return this.options?.membership.current_plan ?? null;
  }

  get upgrades(): PlanChangeOption[] {
    return this.options?.upgrades ?? [];
  }

  get downgrades(): PlanChangeOption[] {
    return this.options?.downgrades ?? [];
  }

  get pendingChange() {
    return this.options?.pending_change ?? null;
  }

  get renewalLabel(): string {
    return this.formatDate(this.options?.cycle?.renewal_date);
  }

  get todayLabel(): string {
    return this.formatDate(new Date().toISOString());
  }

  get immediateSubLabel(): string {
    return this.immediateAllowed
      ? `Switch today, ${this.todayLabel}`
      : 'Not available on the renewal day';
  }

  get processingTitle(): string {
    if (this.timing === 'next_cycle') return 'Scheduling change…';
    return this.isDowngrade ? 'Processing downgrade…' : 'Processing upgrade…';
  }

  /** Where a refund goes. The card comes from the server; never guessed here. */
  get refundDestinationLabel(): string {
    const card = this.options?.refund_destination;
    if (card?.brand && card?.last4) {
      return `Returned to ${card.brand} •••• ${card.last4}`;
    }
    return 'Returned to the card used for the last payment';
  }

  refundChoiceValue(choice: PlanChangeRefundChoice): string {
    if (choice.option !== 'custom') return this.money(choice.amount);
    return this.refundOption === 'custom' && this.customRefundValue !== null
      ? this.money(this.customRefundValue)
      : '—';
  }

  get immediateAllowed(): boolean {
    return this.options?.cycle?.immediate_allowed !== false;
  }

  get isDowngrade(): boolean {
    return this.selected?.direction === 'downgrade';
  }

  get showRefund(): boolean {
    return this.isDowngrade && this.timing === 'immediate' && !!this.selected?.immediate;
  }

  get refundChoices() {
    return this.selected?.immediate?.refund_options ?? [];
  }

  get customRefundMax(): number {
    return this.refundChoices.find(c => c.option === 'custom')?.max_amount ?? 0;
  }

  get customRefundValue(): number | null {
    const n = Number(this.customRefundDraft);
    if (this.customRefundDraft.trim() === '' || !Number.isFinite(n)) return null;
    if (n < 0 || n > this.customRefundMax) return null;
    return Math.round(n * 100) / 100;
  }

  get showWashAllowance(): boolean {
    const wa = this.selected?.immediate?.wash_allowance;
    return this.timing === 'immediate' && !!wa && !wa.unlimited;
  }

  get washOverrideMax(): number {
    return this.selected?.immediate?.wash_allowance.override_max ?? 0;
  }

  get washOverrideValue(): number | null {
    if (this.washOverrideDraft.trim() === '') return null;
    const n = Number(this.washOverrideDraft);
    if (!Number.isInteger(n) || n < 0 || n > this.washOverrideMax) return null;
    return n;
  }

  get washOverrideInvalid(): boolean {
    return this.washOverrideDraft.trim() !== '' && this.washOverrideValue === null;
  }

  selectPlan(option: PlanChangeOption): void {
    if (this.state !== 'select') return;
    this.selected = option;
    this.timing = null;
    this.refundOption = null;
    this.customRefundDraft = '';
    this.washOverrideDraft = '';
    this.requestId = newRequestId();
  }

  isSelected(option: PlanChangeOption): boolean {
    return this.selected?.package_id === option.package_id;
  }

  selectTiming(timing: PlanChangeTiming): void {
    if (timing === 'immediate' && !this.immediateAllowed) return;
    this.timing = timing;
    this.refundOption = null;
    this.customRefundDraft = '';
    const def = this.selected?.immediate?.wash_allowance.washes_this_cycle_default;
    this.washOverrideDraft =
      timing === 'immediate' && this.showWashAllowance && def !== null && def !== undefined
        ? String(def)
        : '';
    this.requestId = newRequestId();
  }

  selectRefund(option: PlanChangeRefundOption): void {
    this.refundOption = option;
    if (option !== 'custom') this.customRefundDraft = '';
    this.requestId = newRequestId();
  }

  onCustomRefundInput(value: string): void {
    this.customRefundDraft = value.replace(/[^0-9.]/g, '');
    this.requestId = newRequestId();
  }

  onWashOverrideInput(value: string): void {
    this.washOverrideDraft = value.replace(/[^0-9]/g, '');
    this.requestId = newRequestId();
  }

  private resetSelection(): void {
    this.selected = null;
    this.timing = null;
    this.refundOption = null;
    this.customRefundDraft = '';
    this.washOverrideDraft = '';
    this.requestId = newRequestId();
  }

  // ---------------------------------------------------------------- summary

  get refundAmount(): number {
    if (!this.showRefund || !this.refundOption) return 0;
    if (this.refundOption === 'custom') return this.customRefundValue ?? 0;
    return this.refundChoices.find(c => c.option === this.refundOption)?.amount ?? 0;
  }

  get summaryRows(): PlanChangeSummaryRow[] {
    const sel = this.selected;
    const cur = this.current;
    if (!sel || !this.timing || !cur) return [];
    const fromRenewal: PlanChangeSummaryRow = {
      label: `From ${this.renewalLabel}`,
      sub: '',
      plan: sel.name,
      value: `${this.money(sel.next_cycle.price_from_renewal)}/mo`,
      valueSub: 'Billed monthly',
      emphasis: true,
    };
    if (this.timing === 'next_cycle') {
      return [
        {
          label: 'Due today',
          sub: `Stays on ${cur.name} until renewal`,
          value: this.money(0),
          emphasis: false,
        },
        fromRenewal,
      ];
    }
    if (sel.direction === 'upgrade') {
      const days = this.options?.cycle?.remaining_days ?? 0;
      return [
        {
          label: 'Charged today',
          sub: `Prorated difference · ${days} day${days === 1 ? '' : 's'} left`,
          value: this.money(sel.immediate?.charged_today ?? 0),
          emphasis: true,
        },
        fromRenewal,
      ];
    }
    const picked =
      !!this.refundOption && (this.refundOption !== 'custom' || this.customRefundValue !== null);
    let sub = 'Select a refund option';
    if (picked) {
      sub = this.refundAmount > 0 ? this.refundDestinationSummary : 'No refund';
    }
    return [
      {
        label: 'Refund today',
        sub,
        value: picked ? this.money(this.refundAmount) : '—',
        emphasis: false,
      },
      fromRenewal,
    ];
  }

  private get refundDestinationSummary(): string {
    const card = this.options?.refund_destination;
    return card?.brand && card?.last4
      ? `To ${card.brand} •••• ${card.last4}`
      : 'To the card used for the last payment';
  }

  get canConfirm(): boolean {
    if (this.state !== 'select' || !this.selected || !this.timing) return false;
    if (this.timing === 'immediate' && !this.selected.immediate) return false;
    if (this.showRefund) {
      if (!this.refundOption) return false;
      if (this.refundOption === 'custom' && this.customRefundValue === null) return false;
    }
    if (this.showWashAllowance && this.washOverrideInvalid) return false;
    return true;
  }

  get ctaLabel(): string {
    const sel = this.selected;
    if (!sel || !this.timing) return 'Confirm update';
    if (this.timing === 'next_cycle') return 'Schedule change';
    if (sel.direction === 'upgrade') {
      return `Charge ${this.money(sel.immediate?.charged_today ?? 0)} & upgrade`;
    }
    return this.refundAmount > 0
      ? `Downgrade & refund ${this.money(this.refundAmount)}`
      : 'Downgrade now';
  }

  // ---------------------------------------------------------------- actions

  confirm(): void {
    if (
      !this.canConfirm ||
      !this.subscriptionId ||
      !this.locationId ||
      !this.selected ||
      !this.timing
    ) {
      return;
    }
    const body: PlanChangeRequest = {
      request_id: this.requestId,
      location_id: this.locationId,
      new_package_id: this.selected.package_id,
      timing: this.timing,
    };
    if (this.showRefund && this.refundOption) {
      body.refund_option = this.refundOption;
      if (this.refundOption === 'custom' && this.customRefundValue !== null) {
        body.custom_refund_amount = this.customRefundValue;
      }
    }
    if (this.showWashAllowance && this.washOverrideValue !== null) {
      const def = this.selected.immediate?.wash_allowance.washes_this_cycle_default;
      if (this.washOverrideValue !== def) body.wash_allowance_override = this.washOverrideValue;
    }
    this.state = 'processing';
    this.errorMessage = null;
    this.planChangeService
      .createChange(this.subscriptionId, body)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          const errBody = (err?.error ?? null) as PlanChangeErrorBody | null;
          this.errorMessage =
            this.copyFor(errBody?.code, errBody?.message) ||
            'The change could not be made. Nothing was charged.';
          this.state = 'select';
          return of(null);
        })
      )
      .subscribe(res => {
        if (!res) return;
        if (res.status === false || !res.data) {
          this.errorMessage = res.message || 'The change could not be made.';
          this.state = 'select';
          return;
        }
        this.result = res.data;
        this.state = 'select';
        // The design shows the outcome on the Active subscription screen, not here.
        this.applied.emit(res.data);
        this.back.emit();
      });
  }

  cancelPending(): void {
    const pending = this.pendingChange;
    if (!pending || !this.subscriptionId || this.isCancelling) return;
    this.isCancelling = true;
    this.errorMessage = null;
    this.planChangeService
      .cancelChange(this.subscriptionId, pending.id)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          const errBody = (err?.error ?? null) as PlanChangeErrorBody | null;
          this.errorMessage =
            this.copyFor(errBody?.code, errBody?.message) ||
            'The scheduled change could not be cancelled.';
          return of(null);
        }),
        finalize(() => {
          this.isCancelling = false;
        })
      )
      .subscribe(res => {
        if (!res || res.status === false) return;
        this.load();
      });
  }

  onBack(): void {
    this.back.emit();
  }

  // ------------------------------------------------------------- formatting

  money(value: number | null | undefined): string {
    return USD.format(Number(value ?? 0));
  }

  formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /** The package's own theme colour, else a stable fallback by position. */
  tileBackground(option: PlanChangeOption, index: number): string {
    return (
      resolveThemeBackground(option.theme_color) ??
      FALLBACK_TILE_BACKGROUNDS[index % FALLBACK_TILE_BACKGROUNDS.length]
    );
  }

  /** Pale themes need dark text; the fallbacks are all dark enough for white. */
  tileIsLight(option: PlanChangeOption, index: number): boolean {
    return option.theme_color
      ? isLightThemeBackground(option.theme_color)
      : isLightThemeBackground(FALLBACK_TILE_BACKGROUNDS[index % FALLBACK_TILE_BACKGROUNDS.length]);
  }

  /** The list price beside a family member's discounted price; null when there is no discount. */
  listPriceLabel(plan: PlanChangePlanSummary | null | undefined): string | null {
    const list = familyListPrice(plan);
    return list === null ? null : this.money(list);
  }

  washesLabel(option: { unlimited: boolean; washes_per_cycle: number | null }): string {
    return option.unlimited ? 'Unlimited' : `${option.washes_per_cycle ?? 0} washes / mo`;
  }

  refundChoiceLabel(option: PlanChangeRefundOption): string {
    switch (option) {
      case 'none':
        return 'No refund';
      case 'prorated':
        return 'Prorated refund';
      case 'full':
        return 'Full refund';
      default:
        return 'Custom amount';
    }
  }

  refundChoiceSub(option: PlanChangeRefundOption): string {
    const days = this.options?.cycle?.remaining_days ?? 0;
    switch (option) {
      case 'none':
        return 'Customer keeps the paid amount as is';
      case 'prorated':
        return `Unused price difference for ${days} day${days === 1 ? '' : 's'}`;
      case 'full':
        return 'Full price difference for this cycle';
      default:
        return `Enter any amount up to ${this.money(this.customRefundMax)}`;
    }
  }

  ineligibleCopy(): string {
    return (
      this.copyFor(this.ineligibleReason, this.errorMessage) || 'This membership cannot be changed.'
    );
  }

  private copyFor(code: string | null | undefined, fallback?: string | null): string | null {
    if (code && REASON_COPY[code]) return REASON_COPY[code];
    return fallback || null;
  }
}
